import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

const Wrapper = styled.div`
  position: relative;
`;

const NavRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;

  @media (min-width: 765px) {
    display: none;
  }
`;

const ArrowButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid var(--bg-300);
  background-color: var(--bg-100);
  color: var(--text-100);
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
  &:not(:disabled):hover {
    background-color: var(--bg-200);
  }
`;

const Grid = styled.div`
  position: relative;
  display: flex;

  overflow-x: auto;
  cursor: grab;
  gap: 20px;

  & > * {
    min-width: 0;
  }

  @media (min-width: 765px) {
    display: grid;
    gap: 8px;
    grid-template-columns: repeat(3, 1fr);
    overflow-x: unset;
    //scroll-snap-type: none;
    cursor: default;
  }
  @media (min-width: 1025px) {
    grid-template-columns: repeat(4, 1fr);
  }
  @media (min-width: 1280px) {
    grid-template-columns: repeat(5, 1fr);
  }
`;

const ScrollableGrid = ({ children }) => {
  const gridRef = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const updateEdges = () => {
    const grid = gridRef.current;
    if (!grid) return;
    setAtStart(grid.scrollLeft <= 0);
    setAtEnd(grid.scrollLeft + grid.clientWidth >= grid.scrollWidth - 1);
  };

  useEffect(() => {
    updateEdges();
    const grid = gridRef.current;
    if (!grid) return;
    grid.addEventListener("scroll", updateEdges);
    window.addEventListener("resize", updateEdges);
    return () => {
      grid.removeEventListener("scroll", updateEdges);
      window.removeEventListener("resize", updateEdges);
    };
  }, [children]);

  const scrollByCard = (direction) => {
    const grid = gridRef.current;
    if (!grid || !grid.firstElementChild) return;
    const cardWidth = grid.firstElementChild.getBoundingClientRect().width;
    grid.scrollBy({ left: direction * (cardWidth + 20), behavior: "smooth" });
  };

  const handleMouseDown = (event) => {
    const grid = gridRef.current;
    grid.isDragging = true;
    grid.startX = event.pageX - grid.offsetLeft;
    grid.scrollLeftStart = grid.scrollLeft;
  };

  const handleMouseMove = (event) => {
    const grid = gridRef.current;
    if (!grid.isDragging) return;
    const x = event.pageX - grid.offsetLeft;
    const walk = (x - grid.startX) * 1.5; // Adjust scrolling speed
    grid.scrollLeft = grid.scrollLeftStart - walk;
  };

  const handleMouseUp = () => {
    const grid = gridRef.current;
    grid.isDragging = false;
  };

  return (
    <Wrapper>
      <Grid
        ref={gridRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseUp}
        onMouseUp={handleMouseUp}
        data-scrollable="true"
      >
        {children}
      </Grid>
      <NavRow>
        <ArrowButton
          onClick={() => scrollByCard(-1)}
          disabled={atStart}
          aria-label="Previous"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path
              d="M15 6l-6 6 6 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </ArrowButton>
        <ArrowButton
          onClick={() => scrollByCard(1)}
          disabled={atEnd}
          aria-label="Next"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path
              d="M9 6l6 6-6 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </ArrowButton>
      </NavRow>
    </Wrapper>
  );
};

export default ScrollableGrid;
